import React, { useEffect, useState, useContext } from "react";
import { Link, useParams, useHistory } from "react-router-dom";

// Bootstrap Components
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";

// Context
import AlertContext from "../context/AlertContext";
import { useLocation } from "../context/LocationContext";

// API
import { getBrandProducts, getAllBrandProducts } from "../api/product";
import { getBrand } from "../api/brand";

// Custom Components
import ProductCard from "../components/ProductCard/ProductCard";
import CustomPagination from "../components/Pagination/Pagination";
import PriceRangeSlider from "../components/PriceRangeSlider/PriceRangeSlider";

// Custom styles
import "../assets/scss/pages/brand.styles.scss";

// Util
import { toggleClass } from "../utils/toggleClass";

// Assets
import FilterIcon from "../assets/images/filter-icon.svg";

export default function Brand() {
  const { slug } = useParams();

  // Products and brand state
  const [products, setProducts] = useState(null);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [brand, setBrand] = useState(null);

  // Loading states
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingBrands, setLoadingBrands] = useState(true);

  // All products for filtering
  const [allProducts, setAllProducts] = useState(null); // Start with null to distinguish from empty array

  // Pagination and filtering
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [categories, setCategories] = useState([]);
  const [priceRange, setPriceRange] = useState(null);
  const [filterResetTrigger, setFilterResetTrigger] = useState(0);

  // Mobile filter state
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const alert = useContext(AlertContext);
  const { userCountry } = useLocation();

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 991);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Mobile filter toggle handler
  const handleMobileFilterToggle = () => {
    setIsMobileFilterOpen(!isMobileFilterOpen);
  };

  // Extract filter options from products data
  const getUniqueWatchBands = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueBands = [...new Set(allProducts.map(product => product.tipoCorrea?.nombre).filter(Boolean))];
    return uniqueBands.sort();
  };

  const getUniqueWatchGenres = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueGenres = [...new Set(allProducts.map(product => product.genero?.nombre).filter(Boolean))];
    return uniqueGenres.sort();
  };

  const getUniqueWatchTypes = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueTypes = [...new Set(allProducts.map(product => product.tipo?.nombre).filter(Boolean))];
    return uniqueTypes.sort();
  };

  const getUniqueWatchCalibers = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueCalibers = [...new Set(allProducts.map(product => product.calibre?.nombre).filter(Boolean))];
    return uniqueCalibers.sort();
  };

  const getUniqueWatchConditions = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueConditions = [...new Set(allProducts.map(product => product.condicion?.nombre).filter(Boolean))];
    return uniqueConditions.sort();
  };

  const getUniqueCaratulas = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueCaratulas = [...new Set(allProducts.map(product => product.colorCaratula?.color).filter(Boolean))];
    return uniqueCaratulas.sort();
  };

  const getUniqueCarcasas = () => {
    if (!allProducts || !Array.isArray(allProducts) || allProducts.length === 0) return [];
    const uniqueCarcasas = [...new Set(allProducts.map(product => product.materialCarcasa?.material).filter(Boolean))];
    return uniqueCarcasas.sort();
  };

  // Fetch products for the current page (server-side pagination)
  useEffect(() => {
    const fetchBrandProducts = async () => {
      setLoadingProducts(true);
      try {
        // Fetch products for current page with server-side pagination
        const response = await getBrandProducts(slug, currentPage, 12, userCountry);

        if (response?.error) {
          console.error("Error fetching brand products:", response.message);
          alert.error("Hubo un error al traer información de productos.");
          setProducts([]);
          setTotalPages(0);
        } else {
          // Set products from the response
          const brandProducts = response.docs || [];
          setProducts(brandProducts);

          // Calculate totalPages if not provided by API
          const apiTotalPages = response.totalPages || 0;
          const calculatedTotalPages = response.totalDocs ? Math.ceil(response.totalDocs / 12) : apiTotalPages;
          setTotalPages(calculatedTotalPages);

          // Log result for debugging
          console.log(`Fetched ${brandProducts.length} products for brand: ${slug} (page ${currentPage}/${calculatedTotalPages})`);
          console.log('Full API Response:', response);
          console.log('Pagination info:', {
            totalPages: calculatedTotalPages,
            apiTotalPages: response.totalPages,
            totalDocs: response.totalDocs,
            currentPage,
            hasNextPage: response.hasNextPage,
            hasPrevPage: response.hasPrevPage,
            limit: response.limit,
            page: response.page
          });

          if (brandProducts.length === 0 && currentPage === 1) {
            console.log("No products found for brand:", slug);
          }
        }

      } catch (error) {
        console.error("Error fetching brand products:", error);
        alert.error("Hubo un error al cargar los productos.");
        setProducts([]);
        setTotalPages(0);
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchBrandProducts();
  }, [slug, currentPage, userCountry, alert]);

  // Fetch ALL products for filtering (separate from pagination)
  useEffect(() => {
    const fetchAllBrandProducts = async () => {
      try {
        // Fetch ALL products for filtering purposes
        const response = await getAllBrandProducts(slug, userCountry);

        if (response?.error) {
          console.error("Error fetching all brand products for filtering:", response.message);
          setAllProducts([]);
        } else {
          const allBrandProducts = response.docs || [];
          setAllProducts(allBrandProducts);
          console.log(`Fetched ${allBrandProducts.length} total products for filtering`);
        }
      } catch (error) {
        console.error("Error fetching all brand products for filtering:", error);
        setAllProducts([]);
      }
    };

    fetchAllBrandProducts();
  }, [slug, userCountry]);

  // Fetch brand data
  useEffect(() => {
    const fetchBrandData = async () => {
      try {
        const response = await getBrand(slug);
        if (response?.error) {
          console.error("Error fetching brand:", response.message);
          alert.error("Hubo un error al traer información de marca.");
        } else {
          setBrand(response.docs);
        }
      } catch (error) {
        console.error("Error fetching brand:", error);
        alert.error("Hubo un error al cargar la marca.");
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrandData();
  }, [slug, alert]);

  // Set original products for filtering (from current page)
  useEffect(() => {
    if (products) {
      setOriginalProducts(products);
    }
  }, [products]);

  // Filter products whenever categories or price range change
  useEffect(() => {
    if (originalProducts.length > 0) {
      filterByReference();
    }
  }, [categories, priceRange, originalProducts]);

  const filterByReference = () => {
    let filtered = originalProducts;

    // Filter by categories
    if (categories.length > 0) {
      filtered = filtered.filter((product) => {
        return categories.some((category) => {
          return (
            category === product.genero?.nombre?.toLowerCase() ||
            category === product.tipoCorrea?.nombre?.toLowerCase() ||
            category === product.tipo?.nombre?.toLowerCase() ||
            category === product.calibre?.nombre?.toLowerCase() ||
            category === product.condicion?.nombre?.toLowerCase() ||
            category === product.colorCaratula?.color?.toLowerCase() ||
            category === product.materialCarcasa?.material?.toLowerCase()
          );
        });
      });
    }

    // Filter by price range (only if we have enough products to show the slider)
    if (priceRange && allProducts && allProducts.length >= 5) {
      filtered = filtered.filter((product) => {
        const price = parseFloat(product.precio || product.price || 0);
        return price >= priceRange.min && price <= priceRange.max;
      });
    }

    setFilteredProducts(filtered);
  };

  const clearFilters = () => {
    const selectedFilters = document.querySelectorAll(
      ".filters__filter__dropdown-menu__item__name.active"
    );
    selectedFilters.forEach((filter) => filter.classList.remove("active"));
    setCategories([]);
    setFilterResetTrigger(prev => prev + 1);
  };

  const handleFilterClick = (filterName, filterType, event) => {
    const filtersDropdownMenu = document.querySelector(
      ".filters__filter-container"
    );
    filtersDropdownMenu?.classList.remove("show");
    event.target.classList.toggle("active");

    const filterValue = filterName.toLowerCase();

    if (categories.includes(filterValue)) {
      setCategories(categories.filter((category) => category !== filterValue));
    } else {
      setCategories([...categories, filterValue]);
    }

    // Close mobile filter menu after selection
    if (isMobile && isMobileFilterOpen) {
      setTimeout(() => {
        setIsMobileFilterOpen(false);
      }, 300);
    }
  };

  // REMOVED: No longer needed since we use direct click handlers



  // Determine which products to display
  const displayProducts = (categories.length > 0 || (priceRange && allProducts && allProducts.length >= 5 && (priceRange.min > 0 || priceRange.max < 100000))) ? filteredProducts : products;

  // Simple pagination logic - show when not filtering and multiple pages exist
  const showPagination = !loadingProducts && totalPages > 1 && categories.length === 0;

  console.log('🔍 Pagination Debug:', {
    loadingProducts,
    totalPages,
    categoriesLength: categories.length,
    showPagination
  });

  const isLoading = loadingProducts || loadingBrands;

  if (isLoading) {
    return (
      <div className="catalogue">
        <Container>
          <p className="py-4">Cargando información...</p>
        </Container>
      </div>
    );
  }

  return (
    <section className="brand-catalogue">
      <section className="products col-12">
        <div className="products__container">
          <div
            className="products__brand__header"
            style={{
              backgroundImage: brand?.[0]?.portada?.url
                ? `url("${encodeURI(brand[0].portada.url)}")`
                : "none",
            }}
          >
            <h1 className="text-center">{brand?.[0]?.nombre}</h1>
          </div>
          <div className="container-fluid">
            <Row>
              <div className="filters col-12 col-lg-3">
                <div className="filters__container">
                  <div
                    className="filters__header"
                    style={{ cursor: isMobile ? "pointer" : "default" }}
                    onClick={isMobile ? handleMobileFilterToggle : undefined}
                  >
                    <img
                      src={FilterIcon}
                      alt="Filtros"
                      className="filters__icon"
                    />
                    <p className="filters__title">Filtros</p>
                    {isMobile && (
                      <span
                        className="d-block d-lg-none ms-3"
                        style={{ fontSize: "1.1rem", fontWeight: "bolder" }}
                      >
                        {isMobileFilterOpen
                          ? "Toca para ocultar"
                          : "Toca para expandir filtros"}
                      </span>
                    )}
                  </div>
                  <div className={`filters__filter-container ${isMobile ? (isMobileFilterOpen ? "show" : "") : ""}`}>
                    <div
                      className="filters__filter filters__filter--clear filter__item"
                      data-filter="all"
                    >
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name filters__filter__name--small"
                          onClick={clearFilters}
                        >
                          Limpiar filtros <i className="far fa-times"></i>
                        </p>
                      </div>
                    </div>

                    {/* Price Range Slider - Only show if 5+ products */}
                    {allProducts && Array.isArray(allProducts) && allProducts.length >= 5 && (
                      <PriceRangeSlider
                        products={allProducts}
                        onPriceChange={setPriceRange}
                        resetTrigger={filterResetTrigger}
                      />
                    )}

                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Correa
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueWatchBands().map((band, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={band.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(band, "tipoCorrea", e)
                                }
                              >
                                <p
                                  className="filters__filter__dropdown-menu__item__name"
                                  style={{ textTransform: "capitalize" }}
                                >
                                  {band}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Género
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueWatchGenres().map((genre, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={genre.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(genre, "genero", e)
                                }
                              >
                                <p className="filters__filter__dropdown-menu__item__name text-capitalize">
                                  {genre}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Condición
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueWatchConditions().map((condition, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={condition.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(condition, "condicion", e)
                                }
                              >
                                <p className="filters__filter__dropdown-menu__item__name text-capitalize">
                                  {condition}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Tipo
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueWatchTypes().map((type, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={type.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(type, "tipo", e)
                                }
                              >
                                <p className="filters__filter__dropdown-menu__item__name">
                                  {type}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Calibre
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueWatchCalibers().map((caliber, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={caliber.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(caliber, "calibre", e)
                                }
                              >
                                <p className="filters__filter__dropdown-menu__item__name">
                                  {caliber}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* NEW: Caratula (Dial Color) Filter */}
                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Color de Carátula
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueCaratulas().map((caratula, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={caratula.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(caratula, "caratula", e)
                                }
                              >
                                <p className="filters__filter__dropdown-menu__item__name">
                                  {caratula}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* NEW: Carcasa (Case Material) Filter */}
                    <div className="filters__filter filters__filter--dropdown">
                      <div className="filters__filter__content">
                        <p
                          className="filters__filter__name"
                          onClick={(e) => {
                            e.stopPropagation();
                            const dropdownMenu = e.target.closest('.filters__filter').querySelector('.filters__filter__dropdown-menu');
                            const filterElement = e.target.closest('.filters__filter');
                            toggleClass(filterElement, "open");
                            toggleClass(dropdownMenu, "open");
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          Material de Carcasa
                        </p>
                        <div className="filters__filter__dropdown-menu">
                          <ul className="filters__filter__dropdown-menu__item-container">
                            {getUniqueCarcasas().map((carcasa, key) => (
                              <li
                                className="filters__filter__dropdown-menu__item filter__item"
                                data-filter={carcasa.toLowerCase()}
                                key={key}
                                onClick={(e) =>
                                  handleFilterClick(carcasa, "carcasa", e)
                                }
                              >
                                <p className="filters__filter__dropdown-menu__item__name">
                                  {carcasa}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="brand-catalogue__products col-12 col-lg-9">
                <Row>
                  {loadingProducts ? (
                    <p className="pt-3">Cargando productos...</p>
                  ) : displayProducts?.length > 0 ? (
                    displayProducts.map((product, key) => (
                      <ProductCard
                        product={product}
                        key={key}
                        loading={loadingProducts}
                      />
                    ))
                  ) : (
                    <p className="py-3">
                      {(categories.length > 0 || (priceRange && originalProducts && originalProducts.length >= 5 && (priceRange.min > 0 || priceRange.max < 100000)))
                        ? "No se encontraron relojes con los filtros seleccionados."
                        : "No se encontraron relojes de esta marca."}
                    </p>
                  )}
                </Row>

                {/* Show pagination when not filtering and there are multiple pages */}
                {showPagination && (
                  <div className="d-flex justify-content-center mt-4">
                    <CustomPagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      paginate={(page) => setCurrentPage(page)}
                      hasNextPage={currentPage < totalPages}
                      hasPrevPage={currentPage > 1}
                    />
                  </div>
                )}
              </div>
            </Row>
          </div>
        </div>
      </section>
    </section>
  );
}
